import { z } from 'zod';
import { addHourlyPremiumIssues } from './schemaHelpers';

const fixedFeeEntrySchema = z.object({
  fee_amount: z.number().nullable().optional(),
}).passthrough();

const mealConfigSchema = z.object({
  applyMealSurcharge: z.boolean().nullable().optional(),
  mealDeadlineHours: z.number().nullable().optional(),
  gracePeriodMinutes: z.number().nullable().optional(),
  mealSurchargeType: z.string().nullable().optional(),
  useAdditionalOffset: z.boolean().nullable().optional(),
  stepUpAfterMinutes: z.number().nullable().optional(),
  creditIntervalMinutes: z.number().nullable().optional(),
  timeCreditMinutes: z.number().nullable().optional(),
  mealTriggerMode: z.string().nullable().optional(),
  fixedFeeSchedule: z.array(fixedFeeEntrySchema).nullable().optional(),
  premiumType: z.string().nullable().optional(),
  premiumValue: z.number().nullable().optional(),
  // Unified rate-offset fields (Phase 9).
  floorSource: z.enum(['none', 'fixed_tier']).nullable().optional(),
  overtimeOffset: z.number().int().min(0).max(10).nullable().optional(),
  floorTierCode: z.string().nullable().optional(),
  floorTierCustomValue: z.number().min(1).max(9.99).nullable().optional(),
}).passthrough();

export const mealBreakSchema = z
  .object({
    mealConfig: mealConfigSchema.nullable().optional(),
    rateMultipliers: z.array(z.object({ code: z.string() }).passthrough()).optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    const mealConfig = data.mealConfig;

    if (!mealConfig?.applyMealSurcharge) return;

    if (!mealConfig.mealDeadlineHours || mealConfig.mealDeadlineHours <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mealConfig.mealDeadlineHours'],
        message: 'Meal Break Deadline must be greater than 0',
      });
    }

    if (mealConfig.gracePeriodMinutes !== null && mealConfig.gracePeriodMinutes !== undefined && mealConfig.gracePeriodMinutes < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mealConfig.gracePeriodMinutes'],
        message: 'Grace period cannot be negative',
      });
    }

    if (!mealConfig.mealSurchargeType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mealConfig.mealSurchargeType'],
        message: 'Please select a surcharge type',
      });
    }

    // Count mode: required_breaks NULL silently produces zero surcharge rows
    // (shift_meal_count_surcharge_view CASE → NULL → filtered out). Block save.
    // additional_break_interval_hours = 0 trips NULLIF in the same view → same dropout.
    if (mealConfig.mealTriggerMode === 'count') {
      const cfg = mealConfig as Record<string, unknown>;
      const requiredBreaks = cfg.requiredBreaks as number | null | undefined;
      const additionalBreakIntervalHours = cfg.additionalBreakIntervalHours as number | null | undefined;

      if (
        requiredBreaks === null ||
        requiredBreaks === undefined ||
        typeof requiredBreaks !== 'number' ||
        !Number.isInteger(requiredBreaks) ||
        requiredBreaks < 1
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mealConfig.requiredBreaks'],
          message: 'Required breaks must be a whole number of at least 1',
        });
      }

      if (
        additionalBreakIntervalHours !== null &&
        additionalBreakIntervalHours !== undefined &&
        (typeof additionalBreakIntervalHours !== 'number' || additionalBreakIntervalHours < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mealConfig.additionalBreakIntervalHours'],
          message: 'Additional break interval must be at least 1 hour',
        });
      }
    }

    if (mealConfig.mealSurchargeType === 'offset_overtime') {
      if (mealConfig.useAdditionalOffset && (mealConfig.stepUpAfterMinutes === null || mealConfig.stepUpAfterMinutes === undefined || mealConfig.stepUpAfterMinutes <= 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mealConfig.stepUpAfterMinutes'],
          message: 'Escalation threshold must be greater than 0',
        });
      }
      // Enforce spec's [1, 10] range for offset_overtime. preset_validity_check
      // rejects 0, and overtime_offset_range_check rejects >10 — surface both
      // at the frontend so users see a field-level error, not a late DB failure.
      const offset = mealConfig.overtimeOffset;
      if (offset === null || offset === undefined || !Number.isInteger(offset) || offset < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mealConfig.overtimeOffset'],
          message: 'Overtime offset must be a whole number of at least 1',
        });
      } else if (offset > 10) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mealConfig.overtimeOffset'],
          message: 'Maximum is 10 tiers per missed break',
        });
      }
    }

    if (mealConfig.mealSurchargeType === 'credit_extra_time') {
      // Credit interval is only relevant in deadline mode (cadence-based)
      if (mealConfig.mealTriggerMode !== 'count') {
        if (!mealConfig.creditIntervalMinutes || mealConfig.creditIntervalMinutes <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mealConfig.creditIntervalMinutes'],
            message: 'Credit Interval must be greater than 0',
          });
        }
      }
      if (!mealConfig.timeCreditMinutes || mealConfig.timeCreditMinutes <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mealConfig.timeCreditMinutes'],
          message: 'Time Credit must be greater than 0',
        });
      }
    }

    if (mealConfig.mealSurchargeType === 'credit_extra_time' && mealConfig.mealTriggerMode === 'count') {
      const basis = (mealConfig as Record<string, unknown>).premiumBasis as string | undefined;
      if (basis && basis !== 'ot_inclusive') {
        if (!(data.rateMultipliers ?? []).some(m => m.code === basis)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['mealConfig.premiumBasis'],
            message: 'Selected credit basis tier no longer exists. Please choose a valid tier.',
          });
        }
      }
    }

    if (mealConfig.mealSurchargeType === 'fixed_fee') {
      if (!mealConfig.fixedFeeSchedule || mealConfig.fixedFeeSchedule.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mealConfig.fixedFeeSchedule'],
          message: 'At least one fixed fee is required',
        });
      } else {
        mealConfig.fixedFeeSchedule.forEach((fee, index) => {
          if (fee.fee_amount === null || fee.fee_amount === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['mealConfig.fixedFeeSchedule'],
              message: `Fee amount for missed break ${index + 1} is required`,
            });
          } else if (typeof fee.fee_amount === 'number') {
            if (isNaN(fee.fee_amount)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['mealConfig.fixedFeeSchedule'],
                message: `Fee for missed break ${index + 1} is not a valid number`,
              });
            } else if (fee.fee_amount < 0.01) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['mealConfig.fixedFeeSchedule'],
                message: `Fee amount for missed break ${index + 1} must be at least $0.01`,
              });
            } else if (fee.fee_amount > 999999) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['mealConfig.fixedFeeSchedule'],
                message: `Fee amount for missed break ${index + 1} cannot exceed $999,999`,
              });
            }
          }
        });
      }
    }

    if (mealConfig.mealSurchargeType === 'hourly_premium') {
      addHourlyPremiumIssues(ctx, mealConfig.premiumType, mealConfig.premiumValue, 'mealConfig.premiumType', 'mealConfig.premiumValue');
    }
  });

export type MealBreakSchemaData = z.infer<typeof mealBreakSchema>;
